import React, { useMemo } from 'react';
import { usePostHogQuery } from '../../hooks/usePostHogQuery';
import { buildTrendsQuery, buildFunnelsQuery } from '../../utils/posthogApi';
import KpiCard from './components/KpiCard';
import TrendChart from './components/TrendChart';
import FunnelChart from './components/FunnelChart';
import { formatDate, parseFunnelSteps } from './utils';

const GrowthRetentionView = ({ dateRange }) => {
  const dateFrom = dateRange || '-30d';

  // ── Signup funnel ──
  const { data: signupFunnel, loading: signupFunnelLoading } = usePostHogQuery(
    () => buildFunnelsQuery(
      ['signup_started', 'signup_completed', 'login_completed'],
      dateFrom,
      { windowDays: 7 }
    ),
    { deps: [dateFrom] }
  );

  // ── Signups over time (daily for chart) ──
  const { data: signupDaily, loading: signupDailyLoading } = usePostHogQuery(
    () => buildTrendsQuery(['signup_completed'], dateFrom),
    { deps: [dateFrom] }
  );

  // ── Activation: new artists who upload within 7d ──
  const { data: artistActivation, loading: artistActivationLoading } = usePostHogQuery(
    () => buildFunnelsQuery(
      ['signup_completed', 'artwork_uploaded'],
      dateFrom,
      { windowDays: 7 }
    ),
    { deps: [dateFrom] }
  );

  // ── Activation: new collectors who like within 7d ──
  const { data: collectorActivation, loading: collectorActivationLoading } = usePostHogQuery(
    () => buildFunnelsQuery(
      ['signup_completed', 'artwork_liked'],
      dateFrom,
      { windowDays: 7 }
    ),
    { deps: [dateFrom] }
  );

  // ── User type breakdown (signups by preferences.home) ──
  const { data: userTypeData, loading: userTypeLoading } = usePostHogQuery(
    () => buildTrendsQuery(
      ['signup_completed'],
      dateFrom,
      {
        interval: 'week',
        breakdownBy: { breakdown: 'preferences__home', breakdown_type: 'person' },
      }
    ),
    { deps: [dateFrom] }
  );

  // ── Derived data ──

  const activationRate = (funnelData) => {
    const steps = parseFunnelSteps(funnelData);
    if (steps.length < 2 || steps[0].count === 0) return '—';
    return ((steps[1].count / steps[0].count) * 100).toFixed(1) + '%';
  };

  const signupChartData = useMemo(() => {
    if (!signupDaily?.results?.[0]) return [];
    const series = signupDaily.results[0];
    return (series.labels || []).map((label, i) => ({
      date: formatDate(label),
      signups: series.data?.[i] ?? 0,
    }));
  }, [signupDaily]);

  const normalizeBreakdown = (value) => {
    if (!value || value === '$$_posthog_breakdown_null_$$') return 'Not set';
    return value;
  };

  const userTypeChartData = useMemo(() => {
    if (!userTypeData?.results?.length) return [];
    const allLabels = userTypeData.results[0]?.labels || [];

    return allLabels.map((label, i) => {
      const point = { date: formatDate(label) };
      userTypeData.results.forEach((series) => {
        const key = normalizeBreakdown(series.breakdown_value);
        point[key] = series.data?.[i] ?? 0;
      });
      return point;
    });
  }, [userTypeData]);

  const userTypeLines = useMemo(() => {
    if (!userTypeData?.results) return [];
    const colors = { artist: '#8b5cf6', collector: '#10b981', 'Not set': '#9ca3af' };
    return userTypeData.results.map((series) => {
      const key = normalizeBreakdown(series.breakdown_value);
      return {
        key,
        label: key,
        color: colors[key] || '#6366f1',
      };
    });
  }, [userTypeData]);

  const totalSignups = signupDaily?.results?.[0]?.count ?? 0;

  return (
    <div className="bi-growth">
      {/* KPI Cards */}
      <div className="bi-kpi-grid">
        <KpiCard title="Total Signups" value={totalSignups} loading={signupDailyLoading} />
        <KpiCard
          title="Artist Activation (7d)"
          value={activationRate(artistActivation)}
          loading={artistActivationLoading}
        />
        <KpiCard
          title="Collector Activation (7d)"
          value={activationRate(collectorActivation)}
          loading={collectorActivationLoading}
        />
      </div>

      {/* Signup Funnel */}
      <FunnelChart
        title="Signup Funnel"
        steps={parseFunnelSteps(signupFunnel)}
        loading={signupFunnelLoading}
      />

      {/* Signups Over Time */}
      <TrendChart
        title="New Signups (Daily)"
        data={signupChartData}
        lines={[{ key: 'signups', label: 'Signups', color: '#6366f1' }]}
        loading={signupDailyLoading}
      />

      {/* User Type Breakdown */}
      <div className="bi-section">
        <h2 className="bi-section-title">User Type Breakdown</h2>
        <TrendChart
          title="Signups by User Type (Weekly)"
          data={userTypeChartData}
          lines={userTypeLines}
          loading={userTypeLoading}
        />
      </div>

      {/* Activation Funnels */}
      <div className="bi-section">
        <h2 className="bi-section-title">Activation Funnels</h2>
        <div className="bi-charts-grid">
          <FunnelChart
            title="Artist Activation (signup → upload in 7d)"
            steps={parseFunnelSteps(artistActivation)}
            loading={artistActivationLoading}
          />
          <FunnelChart
            title="Collector Activation (signup → like in 7d)"
            steps={parseFunnelSteps(collectorActivation)}
            loading={collectorActivationLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default GrowthRetentionView;
